import { useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Line,
  Area,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Plus, UserPlus, ClipboardList, Search, Activity } from 'lucide-react';
import { emptyRoutineDraft, emptyClientDraft } from './demoFixtures';
import { todayYmd, uid, shiftYmd } from './demoStore';

const chartTooltip = {
  background: '#fff',
  border: '1px solid #e2e8f0',
  borderRadius: 8,
  color: '#0f172a',
};
const tick = { fill: '#64748b', fontSize: 11 };

function buildLoadSeriesPlaceholder() {
  const today = todayYmd();
  return Array.from({ length: 6 }, (_, i) => ({
    fecha: shiftYmd(today, (i - 5) * 2),
    label: shiftYmd(today, (i - 5) * 2).slice(5),
    carga: 30 + i * 3,
  }));
}

function trafficLabel(light) {
  if (light === 'green') return 'Verde';
  if (light === 'yellow') return 'Amarillo';
  return 'Rojo';
}

function trafficCardClass(light) {
  if (light === 'green') return 'border-emerald-200 bg-emerald-50';
  if (light === 'yellow') return 'border-amber-200 bg-amber-50';
  return 'border-red-200 bg-red-50';
}

function trafficDotClass(light) {
  if (light === 'green') return 'bg-emerald-500';
  if (light === 'yellow') return 'bg-amber-400';
  return 'bg-red-500';
}

function initials(name = '') {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

function StatCard({ label, value, className = '', valueClass = 'text-stone-900' }) {
  return (
    <div className={`rounded-xl border border-slate-200 bg-white p-4 ${className}`}>
      <p className="text-xs uppercase tracking-wide text-stone-500 font-semibold">{label}</p>
      <p className={`text-3xl font-extrabold mt-1 ${valueClass}`}>{value}</p>
    </div>
  );
}

function DemoTraining({ state, setState }) {
  const view = state.ui.trainingView;
  const clients = state.coach.clients;
  const routines = state.coach.routines;
  const selectedId = state.ui.selectedClientId;
  const selected = clients.find((c) => c.id === selectedId) || clients[0] || null;
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [clientForm, setClientForm] = useState(emptyClientDraft);
  const [assignId, setAssignId] = useState(null);
  const [assignClientId, setAssignClientId] = useState('');
  const [toast, setToast] = useState('');
  const [editingRoutine, setEditingRoutine] = useState(null);

  const setView = (trainingView) => {
    setState((prev) => ({ ...prev, ui: { ...prev.ui, trainingView } }));
  };

  const showToast = (msg) => {
    setToast(msg);
    window.setTimeout(() => setToast(''), 2200);
  };

  const counts = useMemo(() => {
    const tally = { total: clients.length, green: 0, yellow: 0, red: 0 };
    for (const c of clients) {
      const key = String(c.traffic_light || 'red');
      if (tally[key] != null) tally[key] += 1;
    }
    return tally;
  }, [clients]);

  const filteredClients = useMemo(() => {
    const q = search.trim().toLowerCase();
    return clients.filter((c) => {
      const light = String(c.traffic_light || 'red');
      if (filter !== 'all' && light !== filter) return false;
      if (!q) return true;
      return String(c.nombre || '').toLowerCase().includes(q)
        || String(c.email || '').toLowerCase().includes(q);
    });
  }, [clients, search, filter]);

  const openSeguimiento = (clientId) => {
    setState((prev) => ({
      ...prev,
      ui: {
        ...prev.ui,
        selectedClientId: clientId,
        trainingView: 'seguimiento',
        seguimientoTab: 'resumen',
      },
    }));
  };

  const createClient = (e) => {
    e.preventDefault();
    const id = uid('cli');
    const today = todayYmd();
    const nuevo = {
      id,
      nombre: clientForm.nombre || 'Asesorado sin nombre',
      email: clientForm.email || `${id}@demo.local`,
      telefono: clientForm.telefono || '',
      plan: clientForm.plan || 'Mensual Training',
      traffic_light: 'red',
      adherence_pct: 0,
      has_plan: false,
      assigned_routine_id: null,
      last_session: null,
      weight_series: [{ fecha: today, label: today.slice(5), peso: 70 }],
      load_series: [],
      sessions: [],
      wellbeing: { sueño: 5, estrés: 5, energía: 5 },
      athlete_day: { day_name: 'Sin rutina asignada', exercises: [] },
    };
    setState((prev) => ({
      ...prev,
      coach: { ...prev.coach, clients: [nuevo, ...prev.coach.clients] },
      ui: { ...prev.ui, selectedClientId: id, trainingView: 'advisees' },
    }));
    setClientForm(emptyClientDraft());
    showToast('Asesorado creado (demo · sin validaciones ni BD)');
  };

  const assignRoutine = () => {
    if (!assignId || !assignClientId) return;
    const routine = routines.find((r) => r.id === assignId);
    setState((prev) => ({
      ...prev,
      coach: {
        ...prev.coach,
        clients: prev.coach.clients.map((c) => {
          if (c.id !== assignClientId) return c;
          const day = routine?.dias?.[0];
          const exercises = (day?.ejercicios || [])
            .filter((ex) => ex.exercise_name)
            .map((ex, i) => ({
              id: `ae_${i}`,
              nombre: ex.exercise_name,
              sets: Number(ex.sets) || 3,
              reps: ex.reps || '8-12',
              done: Array.from({ length: Number(ex.sets) || 3 }, () => false),
              load: 20,
            }));
          return {
            ...c,
            assigned_routine_id: assignId,
            has_plan: true,
            traffic_light: c.traffic_light === 'red' ? 'yellow' : c.traffic_light,
            athlete_day: {
              day_name: day?.nombre || routine?.nombre || 'Rutina asignada',
              exercises,
            },
          };
        }),
      },
    }));
    setAssignId(null);
    setAssignClientId('');
    showToast('Rutina asignada al asesorado');
  };

  const saveRoutine = () => {
    if (!editingRoutine) return;
    const draft = {
      ...editingRoutine,
      nombre: editingRoutine.nombre || 'Rutina sin nombre',
      dias: editingRoutine.dias.map((d) => ({
        ...d,
        ejercicios: d.ejercicios.map((ex) => ({
          ...ex,
          exercise_name: ex.exercise_name || 'Ejercicio',
        })),
      })),
    };
    setState((prev) => {
      const exists = prev.coach.routines.some((r) => r.id === draft.id);
      const nextRoutines = exists
        ? prev.coach.routines.map((r) => (r.id === draft.id ? draft : r))
        : [draft, ...prev.coach.routines];
      return {
        ...prev,
        coach: { ...prev.coach, routines: nextRoutines },
        ui: { ...prev.ui, trainingView: 'routines' },
      };
    });
    setEditingRoutine(null);
    showToast('Rutina guardada en biblioteca (solo navegador)');
  };

  const toggleAthleteSet = (exId, setIdx) => {
    if (!selected) return;
    setState((prev) => ({
      ...prev,
      coach: {
        ...prev.coach,
        clients: prev.coach.clients.map((c) => {
          if (c.id !== selected.id) return c;
          const exercises = c.athlete_day.exercises.map((ex) => {
            if (ex.id !== exId) return ex;
            const done = [...ex.done];
            done[setIdx] = !done[setIdx];
            return { ...ex, done };
          });
          const completed = exercises.reduce((n, ex) => n + ex.done.filter(Boolean).length, 0);
          const tot = exercises.reduce((n, ex) => n + ex.done.length, 0);
          let load_series = c.load_series || [];
          let adherence_pct = c.adherence_pct;
          let traffic_light = c.traffic_light;
          let sessions = c.sessions;
          let last_session = c.last_session;
          if (tot && completed === tot) {
            const today = todayYmd();
            const avgLoad = Math.round(
              exercises.reduce((s, ex) => s + Number(ex.load || 0), 0) / Math.max(1, exercises.length),
            );
            const point = { fecha: today, label: today.slice(5), carga: avgLoad, reps: 9 };
            load_series = load_series.some((p) => p.fecha === today)
              ? load_series.map((p) => (p.fecha === today ? point : p))
              : [...load_series, point].slice(-12);
            adherence_pct = Math.min(100, adherence_pct + 5);
            traffic_light = adherence_pct >= 75 ? 'green' : adherence_pct >= 45 ? 'yellow' : 'red';
            last_session = today;
            sessions = [
              { id: uid('s'), fecha: today, titulo: c.athlete_day.day_name, completado: true, volumen: avgLoad * 120 },
              ...sessions,
            ].slice(0, 8);
          }
          return {
            ...c,
            athlete_day: { ...c.athlete_day, exercises },
            load_series,
            adherence_pct,
            traffic_light,
            sessions,
            last_session,
          };
        }),
      },
    }));
  };

  return (
    <div className="dashboard-main-view space-y-6 relative">
      {toast ? (
        <div className="absolute right-0 top-0 z-10 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-800 shadow-sm">
          {toast}
        </div>
      ) : null}

      {view === 'home' ? (
        <>
          <header>
            <h1 className="d28d-page-title text-2xl">Inicio</h1>
            <p className="d28d-text-muted text-sm mt-1">
              Resumen operativo y accesos directos — misma interfaz del panel coach.
            </p>
          </header>
          <section className="ef-stat-grid">
            <div className="ef-stat-card text-left">
              <span className="ef-stat-card__label">Clientes activos</span>
              <p className="ef-stat-card__value ef-stat-card__value--metric">{counts.total}</p>
            </div>
            <div className="ef-stat-card text-left">
              <span className="ef-stat-card__label">Verde</span>
              <p className="ef-stat-card__value ef-stat-card__value--metric">{counts.green}</p>
            </div>
            <div className="ef-stat-card text-left border-amber-400/50 bg-amber-500/10">
              <span className="ef-stat-card__label">Requieren atención</span>
              <p className="ef-stat-card__value ef-stat-card__value--metric">{counts.yellow + counts.red}</p>
            </div>
            <button type="button" className="ef-stat-card text-left hover:border-[var(--d28d-yellow)] transition" onClick={() => setView('advisees')}>
              <span className="ef-stat-card__label">Panel del Entrenador</span>
              <span className="ef-stat-card__value text-sm">Mis asesorados →</span>
            </button>
          </section>
          <section className="space-y-3">
            <h2 className="font-bold text-[var(--d28d-text)] text-sm uppercase tracking-wide">Accesos rápidos</h2>
            <div className="ef-quick-grid ef-quick-grid--coach">
              {[
                { id: 'advisees', label: 'Mis asesorados' },
                { id: 'routines', label: 'Editar rutinas' },
                { id: 'clients', label: 'Clientes' },
                { id: 'seguimiento', label: 'Seguimiento' },
              ].map((link) => (
                <button key={link.id} type="button" className="ef-quick-link" onClick={() => setView(link.id)}>
                  <span className="ef-quick-link__label">{link.label}</span>
                </button>
              ))}
            </div>
          </section>
          <section className="training-light-island surface-readable rounded-xl border border-slate-200 p-4">
            <h3 className="font-bold text-stone-900 text-sm mb-2">Flujo típico del demo</h3>
            <ol className="list-decimal pl-5 text-sm text-stone-600 space-y-1">
              <li>Revisa rutinas prediseñadas o crea una nueva.</li>
              <li>Crea un asesorado (sin validaciones en este demo).</li>
              <li>Asigna la rutina al asesorado.</li>
              <li>Abre Seguimiento y marca series como lo haría el cliente.</li>
            </ol>
          </section>
        </>
      ) : null}

      {view === 'advisees' ? (
        <>
          <header>
            <h1 className="d28d-page-title text-2xl">Panel del Entrenador</h1>
            <p className="d28d-text-muted text-sm mt-1">Mis asesorados — seguimiento, planes y adherencia.</p>
          </header>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard label="Total" value={counts.total} />
            <StatCard label="Verde" value={counts.green} className="border-emerald-200 bg-emerald-50/80" valueClass="text-emerald-800" />
            <StatCard label="Amarillo" value={counts.yellow} className="border-amber-200 bg-amber-50/80" valueClass="text-amber-900" />
            <StatCard label="Rojo" value={counts.red} className="border-red-200 bg-red-50/80" valueClass="text-red-800" />
          </div>
          <section className="training-light-island surface-readable rounded-xl border border-slate-200 p-4 space-y-4">
            <div className="flex flex-wrap gap-3 items-center">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" aria-hidden />
                <input
                  type="search"
                  className="input w-full pl-9"
                  placeholder="Buscar por nombre o correo…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <select className="input w-auto min-w-[140px]" value={filter} onChange={(e) => setFilter(e.target.value)}>
                <option value="all">Todos</option>
                <option value="green">Verde</option>
                <option value="yellow">Amarillo</option>
                <option value="red">Rojo</option>
              </select>
            </div>
            <ul className="space-y-3">
              {filteredClients.map((c) => {
                const routine = routines.find((r) => r.id === c.assigned_routine_id);
                return (
                  <li key={c.id} className={`rounded-xl border p-4 ${trafficCardClass(c.traffic_light)}`}>
                    <div className="flex flex-wrap items-start gap-4">
                      <div className="relative shrink-0">
                        <div className="w-12 h-12 rounded-full bg-white border border-stone-200 grid place-items-center font-bold text-stone-800">
                          {initials(c.nombre)}
                        </div>
                        <span className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-white ${trafficDotClass(c.traffic_light)}`} />
                      </div>
                      <div className="flex-1 min-w-[180px]">
                        <h3 className="font-bold text-stone-900">{c.nombre}</h3>
                        <p className="text-sm text-stone-600">{c.email}</p>
                        <p className="text-xs text-stone-500 mt-1">
                          {c.has_plan ? (routine?.nombre || 'Con plan') : 'Sin plan'}
                          {' · '}{c.adherence_pct ?? 0}% adherencia
                          {' · '}{trafficLabel(c.traffic_light)}
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <button type="button" className="btn-secondary text-xs inline-flex items-center gap-1" onClick={() => openSeguimiento(c.id)}>
                          <Activity className="w-3.5 h-3.5" /> Seguimiento
                        </button>
                        <button
                          type="button"
                          className="btn-secondary text-xs inline-flex items-center gap-1"
                          onClick={() => {
                            setAssignId(c.assigned_routine_id || routines[0]?.id || null);
                            setAssignClientId(c.id);
                            setView('routines');
                          }}
                        >
                          <ClipboardList className="w-3.5 h-3.5" /> Editar rutina
                        </button>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        </>
      ) : null}

      {view === 'routines' ? (
        <div className="space-y-4">
          {editingRoutine ? (
            <section className="card training-light-island surface-readable d28d-admin-surface ui-light-panel p-5 space-y-4">
              <header>
                <h1 className="d28d-page-title text-xl text-stone-900">Editor de rutina</h1>
                <p className="text-sm text-stone-500 mt-1">Nombre, nivel, método y ejercicios del día.</p>
              </header>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <label className="text-sm text-stone-600 space-y-1">
                  Nombre
                  <input className="input w-full" value={editingRoutine.nombre} onChange={(e) => setEditingRoutine({ ...editingRoutine, nombre: e.target.value })} placeholder="Ej. Full Body B" />
                </label>
                <label className="text-sm text-stone-600 space-y-1">
                  Nivel
                  <select className="input w-full" value={editingRoutine.level} onChange={(e) => setEditingRoutine({ ...editingRoutine, level: e.target.value })}>
                    <option value="principiante">Principiante</option>
                    <option value="intermedio">Intermedio</option>
                    <option value="avanzado">Avanzado</option>
                  </select>
                </label>
                <label className="text-sm text-stone-600 space-y-1">
                  Método
                  <select className="input w-full" value={editingRoutine.method} onChange={(e) => setEditingRoutine({ ...editingRoutine, method: e.target.value })}>
                    <option value="hipertrofia">Hipertrofia</option>
                    <option value="fuerza">Fuerza</option>
                    <option value="resistencia">Resistencia</option>
                    <option value="mantenimiento">Mantenimiento</option>
                  </select>
                </label>
              </div>
              {(editingRoutine.dias[0]?.ejercicios || []).map((ex, idx) => (
                <div key={ex.id || idx} className="grid grid-cols-1 md:grid-cols-[2fr_0.7fr_0.9fr] gap-3">
                  <label className="text-sm text-stone-600 space-y-1">
                    Ejercicio
                    <input
                      className="input w-full"
                      value={ex.exercise_name}
                      onChange={(e) => {
                        const ejercicios = editingRoutine.dias[0].ejercicios.map((row, i) => (i === idx ? { ...row, exercise_name: e.target.value } : row));
                        setEditingRoutine({ ...editingRoutine, dias: [{ ...editingRoutine.dias[0], ejercicios }] });
                      }}
                    />
                  </label>
                  <label className="text-sm text-stone-600 space-y-1">
                    Series
                    <input
                      className="input w-full"
                      type="number"
                      min="1"
                      value={ex.sets}
                      onChange={(e) => {
                        const ejercicios = editingRoutine.dias[0].ejercicios.map((row, i) => (i === idx ? { ...row, sets: Number(e.target.value) || 1 } : row));
                        setEditingRoutine({ ...editingRoutine, dias: [{ ...editingRoutine.dias[0], ejercicios }] });
                      }}
                    />
                  </label>
                  <label className="text-sm text-stone-600 space-y-1">
                    Reps
                    <input
                      className="input w-full"
                      value={ex.reps}
                      onChange={(e) => {
                        const ejercicios = editingRoutine.dias[0].ejercicios.map((row, i) => (i === idx ? { ...row, reps: e.target.value } : row));
                        setEditingRoutine({ ...editingRoutine, dias: [{ ...editingRoutine.dias[0], ejercicios }] });
                      }}
                    />
                  </label>
                </div>
              ))}
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className="btn-secondary text-sm inline-flex items-center gap-1"
                  onClick={() => {
                    const ejercicios = [
                      ...editingRoutine.dias[0].ejercicios,
                      { id: uid('ex'), exercise_name: '', sets: 3, reps: '8-12', rest_seconds: 90, intensity_type: 'RPE', intensity_value: 7, notes: '' },
                    ];
                    setEditingRoutine({ ...editingRoutine, dias: [{ ...editingRoutine.dias[0], ejercicios }] });
                  }}
                >
                  <Plus size={14} /> Añadir ejercicio
                </button>
                <button type="button" className="btn-primary text-sm" onClick={saveRoutine}>Guardar rutina</button>
                <button type="button" className="btn-secondary text-sm" onClick={() => setEditingRoutine(null)}>Cancelar</button>
              </div>
            </section>
          ) : (
            <section className="card training-light-island surface-readable d28d-admin-surface ui-light-panel p-5 space-y-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h1 className="d28d-page-title text-xl text-stone-900">Biblioteca de rutinas</h1>
                  <p className="text-sm text-stone-500 mt-1">Prediseñadas + las que crees. Asigna a un asesorado.</p>
                </div>
                <button type="button" className="btn-primary text-sm inline-flex items-center gap-1" onClick={() => setEditingRoutine(emptyRoutineDraft())}>
                  <Plus size={14} /> Nueva rutina
                </button>
              </div>
              <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-stone-500 text-xs uppercase tracking-wide">
                    <tr>
                      <th className="text-left p-3 font-semibold">Nombre</th>
                      <th className="text-left p-3 font-semibold">Nivel</th>
                      <th className="text-left p-3 font-semibold">Método</th>
                      <th className="text-left p-3 font-semibold">Origen</th>
                      <th className="text-left p-3 font-semibold">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {routines.map((r) => (
                      <tr key={r.id} className="border-t border-slate-100">
                        <td className="p-3 font-medium text-stone-900">{r.nombre}</td>
                        <td className="p-3 text-stone-600">{r.level}</td>
                        <td className="p-3 text-stone-600">{r.method}</td>
                        <td className="p-3 text-stone-600">{r.prediseñada ? 'Prediseñada' : 'Creada en demo'}</td>
                        <td className="p-3">
                          <div className="flex flex-wrap gap-2">
                            <button type="button" className="btn-secondary text-xs" onClick={() => setEditingRoutine(JSON.parse(JSON.stringify(r)))}>Editar</button>
                            <button
                              type="button"
                              className="btn-primary text-xs"
                              onClick={() => {
                                setAssignId(r.id);
                                setAssignClientId(selectedId || clients[0]?.id || '');
                              }}
                            >
                              Asignar
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {assignId ? (
            <section className="training-light-island surface-readable rounded-xl border border-emerald-200 bg-emerald-50/50 p-4 space-y-3">
              <h3 className="font-bold text-stone-900">Asignar rutina</h3>
              <p className="text-sm text-stone-600">
                Rutina: <strong>{routines.find((r) => r.id === assignId)?.nombre}</strong>
              </p>
              <label className="text-sm text-stone-600 space-y-1 block max-w-sm">
                Asesorado
                <select className="input w-full" value={assignClientId} onChange={(e) => setAssignClientId(e.target.value)}>
                  <option value="">Selecciona…</option>
                  {clients.map((c) => (
                    <option key={c.id} value={c.id}>{c.nombre}</option>
                  ))}
                </select>
              </label>
              <div className="flex flex-wrap gap-2">
                <button type="button" className="btn-primary text-sm" onClick={assignRoutine}>Confirmar asignación</button>
                <button type="button" className="btn-secondary text-sm" onClick={() => setAssignId(null)}>Cancelar</button>
              </div>
            </section>
          ) : null}
        </div>
      ) : null}

      {view === 'clients' ? (
        <section className="dashboard-main-view surface-readable d28d-admin-surface space-y-4">
          <header>
            <h1 className="d28d-page-title text-2xl text-stone-900">Crear asesorado</h1>
            <p className="text-sm text-stone-500 mt-1">
              En la plataforma real hay validaciones y activación. Aquí puedes crear sin restricciones.
            </p>
          </header>
          <form className="bg-white rounded-3xl shadow-sm border border-slate-200 p-6 ui-light-panel grid grid-cols-1 md:grid-cols-2 gap-4" onSubmit={createClient}>
            <label className="text-sm text-stone-600 space-y-1">
              Nombre
              <input className="input w-full" value={clientForm.nombre} onChange={(e) => setClientForm({ ...clientForm, nombre: e.target.value })} placeholder="Nombre completo" />
            </label>
            <label className="text-sm text-stone-600 space-y-1">
              Email
              <input className="input w-full" value={clientForm.email} onChange={(e) => setClientForm({ ...clientForm, email: e.target.value })} placeholder="correo@ejemplo.com" />
            </label>
            <label className="text-sm text-stone-600 space-y-1">
              Teléfono
              <input className="input w-full" value={clientForm.telefono} onChange={(e) => setClientForm({ ...clientForm, telefono: e.target.value })} placeholder="Opcional" />
            </label>
            <label className="text-sm text-stone-600 space-y-1">
              Plan de activación
              <select className="input w-full" value={clientForm.plan} onChange={(e) => setClientForm({ ...clientForm, plan: e.target.value })}>
                <option>Mensual Training</option>
                <option>Bimestral Training</option>
                <option>Trimestral Training</option>
              </select>
            </label>
            <div className="md:col-span-2">
              <button type="submit" className="btn-primary inline-flex items-center gap-2">
                <UserPlus size={14} /> Crear asesorado
              </button>
            </div>
          </form>
        </section>
      ) : null}

      {view === 'seguimiento' && selected ? (
        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4">
          <aside className="card training-light-island surface-readable ui-light-panel p-4 space-y-3 max-h-[75vh] overflow-y-auto bg-white">
            <h2 className="font-bold text-stone-900 text-sm uppercase tracking-wide">Clientes</h2>
            <ul className="space-y-2">
              {clients.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    className={`w-full text-left rounded-xl border p-3 flex gap-2 items-center ${c.id === selected.id ? 'border-stone-900 bg-stone-50' : 'border-slate-200 bg-white'}`}
                    onClick={() => openSeguimiento(c.id)}
                  >
                    <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${trafficDotClass(c.traffic_light)}`} />
                    <span className="min-w-0">
                      <span className="block font-semibold text-stone-900 text-sm truncate">{c.nombre}</span>
                      <span className="block text-xs text-stone-500">{c.adherence_pct}% · {trafficLabel(c.traffic_light)}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </aside>

          <section className="card training-light-island surface-readable ui-light-panel p-4 md:p-5 space-y-4 bg-white">
            <header>
              <h1 className="text-xl font-bold text-stone-900">{selected.nombre}</h1>
              <p className="text-sm text-stone-500">
                {selected.email} · {selected.has_plan ? 'Con plan' : 'Sin plan'} · Última sesión {selected.last_session || '—'}
              </p>
            </header>
            <div className="flex flex-wrap gap-2">
              {['resumen', 'medidas', 'progresion', 'sesiones', 'vista_cliente'].map((tab) => (
                <button
                  key={tab}
                  type="button"
                  className={state.ui.seguimientoTab === tab ? 'btn-primary text-xs' : 'btn-secondary text-xs'}
                  onClick={() => setState((prev) => ({ ...prev, ui: { ...prev.ui, seguimientoTab: tab } }))}
                >
                  {tab === 'vista_cliente' ? 'Vista cliente' : tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>

            {state.ui.seguimientoTab === 'resumen' ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard label="Adherencia" value={`${selected.adherence_pct}%`} />
                <StatCard label="Semáforo" value={trafficLabel(selected.traffic_light)} />
                <StatCard label="Sueño" value={`${selected.wellbeing?.sueño}/10`} />
                <StatCard label="Energía" value={`${selected.wellbeing?.energía}/10`} />
                <p className="col-span-full text-sm text-stone-600">
                  Rutina: {routines.find((r) => r.id === selected.assigned_routine_id)?.nombre || 'Sin asignar'}
                </p>
              </div>
            ) : null}

            {state.ui.seguimientoTab === 'medidas' ? (
              <div className="h-60">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={selected.weight_series}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="label" tick={tick} />
                    <YAxis domain={['dataMin - 1', 'dataMax + 1']} tick={tick} width={36} />
                    <Tooltip contentStyle={chartTooltip} />
                    <Area type="monotone" dataKey="peso" stroke="#64ba30" fill="rgba(100,186,48,0.2)" />
                    <Line type="monotone" dataKey="peso" stroke="#64ba30" strokeWidth={2} dot={{ r: 3 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            ) : null}

            {state.ui.seguimientoTab === 'progresion' ? (
              <div className="h-60">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={selected.load_series?.length ? selected.load_series : buildLoadSeriesPlaceholder()}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="label" tick={tick} />
                    <YAxis tick={tick} width={36} />
                    <Tooltip contentStyle={chartTooltip} />
                    <Bar dataKey="carga" fill="#f59e0b" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : null}

            {state.ui.seguimientoTab === 'sesiones' ? (
              <ul className="divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white">
                {(selected.sessions || []).length === 0 ? (
                  <li className="p-4 text-sm text-stone-500">Sin sesiones aún</li>
                ) : selected.sessions.map((s) => (
                  <li key={s.id} className="p-3 flex justify-between gap-3 text-sm text-stone-700">
                    <span>{s.fecha} · {s.titulo}</span>
                    <span className="text-stone-500">{s.completado ? 'Completada' : 'Incompleta'}</span>
                  </li>
                ))}
              </ul>
            ) : null}

            {state.ui.seguimientoTab === 'vista_cliente' ? (
              <div className="space-y-3">
                <p className="text-sm text-stone-500">
                  Así ve el asesorado su día. Al completar el día sube adherencia y aparece en sesiones.
                </p>
                {!selected.athlete_day?.exercises?.length ? (
                  <div className="text-center py-8 border border-dashed border-slate-300 rounded-xl">
                    <p className="text-stone-700 font-medium">Sin rutina asignada</p>
                    <button type="button" className="btn-primary mt-3 text-sm" onClick={() => setView('routines')}>Ir a Rutinas</button>
                  </div>
                ) : (
                  <>
                    <h3 className="font-bold text-stone-900">{selected.athlete_day.day_name}</h3>
                    <ul className="space-y-3">
                      {selected.athlete_day.exercises.map((ex) => (
                        <li key={ex.id} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                          <div className="flex justify-between gap-2 text-sm mb-2">
                            <strong className="text-stone-900">{ex.nombre}</strong>
                            <span className="text-stone-500">{ex.sets}×{ex.reps} · {ex.load} kg</span>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {ex.done.map((ok, i) => (
                              <button
                                key={`${ex.id}-${i}`}
                                type="button"
                                className={ok ? 'btn-primary text-xs' : 'btn-secondary text-xs'}
                                onClick={() => toggleAthleteSet(ex.id, i)}
                              >
                                Serie {i + 1}
                              </button>
                            ))}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            ) : null}
          </section>
        </div>
      ) : null}

      {view === 'seguimiento' && !selected ? (
        <section className="training-light-island surface-readable rounded-xl border border-slate-200 p-8 text-center">
          <p className="text-stone-700 font-medium mb-3">Crea un asesorado primero.</p>
          <button type="button" className="btn-primary" onClick={() => setView('clients')}>Ir a Clientes</button>
        </section>
      ) : null}
    </div>
  );
}

export default DemoTraining;
